<template>
  <div class="main-collection container critical-component-clear">
    <div v-if="activeCampaign && enteredCode && !hideHint" class="grid">
      <hint-message
        class="main-collection__hint-message col xs-span l6"
        center-icon
        :content="activeCampaignMessage"
      >
        <template #icon>
          <icon-check-fill />
        </template>
      </hint-message>
    </div>

    <badge-filter
      v-if="hasApplicableFilters"
      show-filter
      :filter-options="filterOptions"
      :filters="filters"
      sorting="Ascending"
      :loading="false"
      prefix="-"
      suffix="%"
      :items-count="getFilteredProducts.length"
      total-items-label=""
      :no-result-message="$string('collection.no_products_filter_html')"
      @reset-filters="resetFilters"
      @toggle-filter="
        (payload) => toggleBadgeFilter(payload.key, payload.option)
      "
    />

    <product-grid
      :collection-product-count="productCount"
      :default-sort-by="$variable('collection.sortBy')"
      :disturber="disturber"
      :handle="metaobjectHandle || $variable('page.handle')"
      :products-per-page="$variable('settings.pagination.products')"
      :template="metaobjectHandle ? 'custom' : 'collection'"
      :hide-soldout="hideSoldout"
      :hide-banner="hideBanner"
      :infinite-scroll="infiniteScroll"
      :filtered-products="getFilteredProducts"
      @current-products="getFilterOptions"
    />
  </div>
</template>

<script>
/**
 * Vue: Main collection (main-collection)
 * -----------------------------------------------------------------------------
 * Main collection section used on the PLP.
 */
import { mapState } from 'pinia';
import useDiscountCampaignsStore from '~stores/discount-campaigns';

import HintMessage from '~async/hint-message/hint-message';
import ProductGrid from '~async/product-grid/product-grid';
import BadgeFilter from '~async/badge-filter/badge-filter';

import IconCheckFill from '~icons/general/check-fill.svg';

import {
  buildFilterOptions,
  filteredProducts,
  getPageNumber,
  parseURLParams,
  updateURL,
} from '~/helpers/filter';

import { getCampaignCode } from '~/helpers/discounts';
import useCartStore from '~stores/cart';

export default {
  name: 'MainCollection',

  components: {
    BadgeFilter,
    HintMessage,
    IconCheckFill,
    ProductGrid,
  },

  provide() {
    return {
      variantsShowCount: this.variantsShowCount,
      variantsShowQatcOverlay: this.variantsShowQatcOverlay,
    };
  },

  props: {
    disturber: {
      type: Array,
      default: () => [],
    },
    productCount: {
      type: Number,
      default: 0,
    },
    metaobjectHandle: {
      type: String,
      default: '',
    },
    hideSoldout: {
      type: Boolean,
      default: false,
    },
    infiniteScroll: {
      type: Boolean,
      default: true,
    },
    hideBanner: {
      type: Boolean,
      default: false,
    },
    hideHint: {
      type: Boolean,
      default: false,
    },
    showFilter: {
      type: Boolean,
      default: true,
    },
    variantsShowCount: {
      type: Boolean,
      default: true,
    },
    variantsShowQatcOverlay: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      filterOptions: {},
      filters: {},
      removeFilters: {},
      currentProducts: [],
      pageNumber: 1,
    };
  },

  computed: {
    /**
     * Map Pinia state.
     */
    ...mapState(useDiscountCampaignsStore, {
      activeCampaign: 'active',
      enteredCode: 'enteredCode',
    }),

    ...mapState(useCartStore, {
      cart: 'response',
    }),

    hasApplicableFilters() {
      const hasApplicableFilters =
        Object.keys(this.filterOptions).length > 0
          ? Boolean(this.filterOptions.Discount.length > 0)
          : false;
      return Boolean(
        hasApplicableFilters &&
          this.showFilter &&
          Object.keys(this.activeCampaign).length > 0,
      );
    },

    getFilteredProducts() {
      return filteredProducts(this.currentProducts, this.filters);
    },

    /**
     * Compute active campaign message.
     * @returns {String}
     */
    activeCampaignMessage() {
      if (!this.activeCampaign || !getCampaignCode) {
        return '';
      }

      const discount = `${this.activeCampaign?.maxDiscount}%`;

      const string = this.$string(
        'discount_campaigns.collection_message_html',
        {
          // Decoding '50%' throws an error
          decode: false,
          replace: {
            code: getCampaignCode(),
            discount,
          },
        },
      );

      return string?.replace(
        '<strong',
        '<strong class="text-mobile-paragraph-m-bold text-desktop-paragraph-s-bold"',
      );
    },
  },

  watch: {
    'cart.discount_codes': {
      immediate: true,
      handler() {
        if (this.activeCampaign) {
          this.filterOptions = buildFilterOptions(this.currentProducts);
        }
      },
    },
  },

  mounted() {
    this.filterOptions = buildFilterOptions(this.currentProducts);
    this.pageNumber = getPageNumber(this.filterOptions, this.filters);
    this.filters = parseURLParams(this.filterOptions);
  },

  methods: {
    /**
     * Get filter options.
     */
    getFilterOptions(products) {
      if (!this.showFilter) {
        return;
      }

      this.currentProducts = products;
      this.filterOptions = buildFilterOptions(products);
    },

    /**
     * Reset filters.
     */
    resetFilters() {
      this.removeFilters = { ...this.filters };
      this.filters = {};

      updateURL(this.removeFilters, this.pageNumber, this.filters);
      this.removeFilters = {};
    },

    /**
     * Toggle badge filter.
     * @param {String} key
     * @param {String} option
     */
    toggleBadgeFilter(key, value) {
      if (!this.filters[key]) {
        this.filters[key] = [];
      }

      this.filters[key] = [value];
      updateURL(this.removeFilters, this.pageNumber, this.filters);
      this.removeFilters = {};
    },
  },
};
</script>

<style lang="scss">
@import './main-collection';
</style>
